import { useState } from "react";

// Community-reported wind hold / closure / hazard flags per resort — mirrors Diver's Horizon's
// SiteStatusBanner.jsx, adapted to this app's class-based (not inline-style) component conventions.
const FLAG_OPTIONS = [
  { key: "wind_hold", label: "Wind hold" },
  { key: "closure", label: "Lift/trail closed" },
  { key: "hazard", label: "Hazard" },
  { key: "other", label: "Other" },
];

const FLAG_BADGE_CLASS = {
  wind_hold: "badge-warn",
  closure: "badge-bad",
  hazard: "badge-bad",
  other: "badge-info",
};

function flagLabel(flagType) {
  return FLAG_OPTIONS.find((f) => f.key === flagType)?.label || "Issue";
}

function relativeTime(iso) {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * @param {{
 *   status: {flagType: string, message: string, reportedByName: string, createdAt: string} | null,
 *   canReport: boolean,
 *   onReport: (flagType: string, message: string) => Promise<void>,
 *   onClear: () => Promise<void>,
 * }} props
 */
export function ResortStatusBanner({ status, canReport, onReport, onClear }) {
  const [formOpen, setFormOpen] = useState(false);
  const [flagType, setFlagType] = useState("wind_hold");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!message.trim() || busy) return;
    setBusy(true);
    try {
      await onReport(flagType, message.trim());
      setFormOpen(false);
      setMessage("");
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (busy) return;
    setBusy(true);
    try {
      await onClear();
    } finally {
      setBusy(false);
    }
  }

  if (status) {
    return (
      <div className="status-flag-banner">
        <div className="status-flag-head">
          <span className={FLAG_BADGE_CLASS[status.flagType] || "badge-info"}>{flagLabel(status.flagType)}</span>
          <span className="status-flag-meta">
            {status.reportedByName || "A local"} · {relativeTime(status.createdAt)}
          </span>
        </div>
        <p className="status-flag-msg">{status.message}</p>
        {canReport && (
          <button className="pill" disabled={busy} onClick={clear}>
            {busy ? "Clearing…" : "Mark resolved"}
          </button>
        )}
      </div>
    );
  }

  if (!canReport) return null;

  if (!formOpen) {
    return (
      <button className="pill status-flag-trigger" onClick={() => setFormOpen(true)}>
        Report an issue
      </button>
    );
  }

  return (
    <div className="status-flag-form">
      <div className="pill-row">
        {FLAG_OPTIONS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`pill${flagType === f.key ? " on" : ""}`}
            onClick={() => setFlagType(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 300))}
        placeholder="What's going on? (e.g. gondola on wind hold above midway)"
        rows={2}
      />
      <div className="status-flag-actions">
        <button className="btn-accent" disabled={!message.trim() || busy} onClick={submit}>
          {busy ? "Posting…" : "Post report"}
        </button>
        <button
          className="pill"
          onClick={() => {
            setFormOpen(false);
            setMessage("");
          }}
        >
          Cancel
        </button>
      </div>
      <p className="sec-dek status-flag-hint">Visible to everyone for 24 hours, or until someone marks it resolved.</p>
    </div>
  );
}
